import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const StadoFAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const faqData = [
    {
      q: "Does Stado Payroll support GPS tracking for field employees?",
      a: "Yes, Stado Payroll includes a robust GPS tracking module that provides live location monitoring, route history playback, and movement logs for all field staff."
    },
    {
      q: "Is Stado Payroll suitable for small businesses?",
      a: "Absolutely. Stado is built to scale. It works perfectly for teams as small as 5 while supporting enterprises managing 5,000+ employees."
    },
    {
      q: "How secure is employee data on Stado?",
      a: "Stado utilizes role-based access control. Employees view their own data securely, and only authorized admins can edit records — ensuring maximum data protection."
    },
    {
      q: "Can Stado Payroll integrate with existing systems?",
      a: "Stado is designed with API-ready architecture and flexible import tools, allowing seamless migration and integration with legacy attendance systems."
    },
    {
      q: "Can Stado handle multi-location teams?",
      a: "Yes, Stado Payroll is built for multi-branch operations. You can manage attendance, payroll, and reporting across multiple cities from one dashboard."
    }
  ];

  return (
    <section className="faq-outer">
      <style>{`
  .faq-outer {
    padding: 80px 5%;
    background: #fafafa;
    font-family: 'Inter', -apple-system, sans-serif;
  }
  .faq-container { max-width: 850px; margin: 0 auto; }
  
  .faq-header { text-align: center; margin-bottom: 50px; }
  .faq-header h2 { 
    font-size: clamp(28px, 5vw, 42px); 
    font-weight: 900; 
    color: #0f172a; 
    margin-bottom: 10px;
  }
  .pink-gradient {
    background: linear-gradient(90deg, #ff3b69, #ff914d);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .faq-list { display: flex; flex-direction: column; gap: 20px; }

  /* ===== SHINY MOVING BORDER ===== */
  .faq-card-wrapper {
    position: relative;
    border-radius: 24px;
    padding: 2px;
    overflow: hidden;
    transition: transform 0.3s ease;
  }

  /* Rotating gradient ring */
  .faq-card-wrapper.active::before {
    content: '';
    position: absolute;
    inset: -60%;
    background: conic-gradient(
      from 0deg,
      transparent 0%,
      #ff3b69 20%,
      #ff914d 40%,
      #ffd1e0 50%,
      #ff914d 60%,
      #ff3b69 80%,
      transparent 100%
    );
    animation: rotateBorder 4s linear infinite;
  }

  /* Shimmer sweep */
  .faq-card-wrapper.active::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      120deg,
      transparent 30%,
      rgba(255,255,255,0.6) 50%,
      transparent 70%
    );
    animation: shineSweep 2.5s ease-in-out infinite;
    pointer-events: none;
  }

  @keyframes rotateBorder {
    100% { transform: rotate(360deg); }
  }

  @keyframes shineSweep {
    0% { transform: translateX(-120%); }
    50% { transform: translateX(120%); }
    100% { transform: translateX(120%); }
  }

  .faq-card {
    position: relative;
    background: #fff;
    border-radius: 22px;
    cursor: pointer;
    z-index: 1;
    width: 100%;
    box-shadow: 0 4px 15px rgba(0,0,0,0.03);
    transition: box-shadow 0.3s ease;
  }

  /* Glow when active */
  .faq-card-wrapper.active .faq-card {
    box-shadow:
      0 0 0 1px rgba(255,59,105,0.2),
      0 0 25px rgba(255,59,105,0.25),
      0 0 45px rgba(255,145,77,0.25);
  }

  .faq-q-row {
    padding: 24px 28px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 15px;
  }

  .faq-q-row h3 {
    font-size: clamp(15px, 2vw, 18px);
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    line-height: 1.4;
  }

  .icon-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
    font-size: 18px;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }

  .active .icon-circle {
    background: linear-gradient(135deg, #ff3b69, #ff914d);
    color: white;
    transform: rotate(135deg) scale(1.05);
    box-shadow: 0 0 15px rgba(255,59,105,0.6);
  }

  .faq-answer-text {
    padding: 0 28px 24px 28px;
    color: #475569;
    line-height: 1.6;
    font-size: clamp(14px, 1.5vw, 16px);
  }

  @media (max-width: 600px) {
    .faq-outer { padding: 50px 15px; }
    .faq-q-row { padding: 18px 20px; }
  }
`}</style>

      <div className="faq-container">
        <div className="faq-header">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Any <span className="pink-gradient">Questions?</span>
          </motion.h2>
        </div>

        <div className="faq-list">
          {faqData.map((item, i) => (
            <motion.div
              key={i}
              className={`faq-card-wrapper ${activeIndex === i ? 'active' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
            >
              <div
                className="faq-card"
                onClick={() => setActiveIndex(activeIndex === i ? null : i)}
              >
                <div className="faq-q-row">
                  <h3>{item.q}</h3>
                  <div className="icon-circle">+</div>
                </div>

                <AnimatePresence>
                  {activeIndex === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <div className="faq-answer-text">
                        {item.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StadoFAQ;